import React from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { Select } from "@/components/Select";

export interface PrioritySelectProps {
    value: number | string;
    onChange: (value: number) => void;
    label?: string;
    full?: boolean;
    className?: string;
}

export const PRIORITIES = [
    {
        value: 1,
        tag: "1",
        arrows: 1,
        color: "text-slate-400 dark:text-zinc-400",
    },
    {
        value: 2,
        tag: "2",
        arrows: 2,
        color: "text-blue-500 dark:text-blue-400",
    },
    {
        value: 3,
        tag: "3",
        arrows: 3,
        color: "text-amber-500 dark:text-amber-400",
    },
    {
        value: 4,
        tag: "4",
        arrows: 4,
        color: "text-rose-500 dark:text-rose-400",
    },
];

export const PRIORITY_CONFIG: Record<
    number | string,
    { arrows: number; color: string; tag: string }
> = {
    1: { arrows: 1, color: "text-slate-400 dark:text-zinc-400", tag: "1" },
    2: { arrows: 2, color: "text-blue-500 dark:text-blue-400", tag: "2" },
    3: { arrows: 3, color: "text-amber-500 dark:text-amber-400", tag: "3" },
    4: { arrows: 4, color: "text-rose-500 dark:text-rose-400", tag: "4" },
    "1": { arrows: 1, color: "text-slate-400 dark:text-zinc-400", tag: "1" },
    "2": { arrows: 2, color: "text-blue-500 dark:text-blue-400", tag: "2" },
    "3": { arrows: 3, color: "text-amber-500 dark:text-amber-400", tag: "3" },
    "4": { arrows: 4, color: "text-rose-500 dark:text-rose-400", tag: "4" },
    low: { arrows: 1, color: "text-slate-400 dark:text-zinc-400", tag: "1" },
    medium: { arrows: 2, color: "text-blue-500 dark:text-blue-400", tag: "2" },
    high: { arrows: 3, color: "text-amber-500 dark:text-amber-400", tag: "3" },
    urgent: { arrows: 4, color: "text-rose-500 dark:text-rose-400", tag: "4" },
    baja: { arrows: 1, color: "text-slate-400 dark:text-zinc-400", tag: "1" },
    media: { arrows: 2, color: "text-blue-500 dark:text-blue-400", tag: "2" },
    alta: { arrows: 3, color: "text-amber-500 dark:text-amber-400", tag: "3" },
    urgente: { arrows: 4, color: "text-rose-500 dark:text-rose-400", tag: "4" },
};

export const PrioritySelect: React.FC<PrioritySelectProps> = ({
    value,
    onChange,
    label,
    full = true,
    className = "",
}) => {
    const { t } = useTranslation("global");

    const numericValue =
        typeof value === "number"
            ? value
            : PRIORITY_CONFIG[value]?.arrows ||
              parseInt(value as string, 10) ||
              2;

    const options = PRIORITIES.map((priority) => ({
        value: priority.value,
        label: t(`priorities.${priority.value}`),
        iconPosition: "left" as const,
        icon: (
            <span
                className={`w-14 shrink-0 inline-flex items-center justify-start gap-0 ${priority.color}`}
            >
                {Array.from({ length: priority.arrows }).map((_, index) => (
                    <Icon
                        key={index}
                        value="arrowUp"
                        size={14}
                        strokeWidth={3}
                    />
                ))}
            </span>
        ),
    }));

    return (
        <div className={full ? "w-full" : ""}>
            {label && (
                <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1.5">
                    {label}
                </label>
            )}
            <Select
                value={numericValue}
                onChange={(val) => onChange(Number(val))}
                options={options}
                className={className}
                full={full}
            />
        </div>
    );
};
